import {
  CHUNK_RELOAD_KEY,
  getErrorMessage,
  isAssetUrl,
  isChunkOrAssetFailure,
  NETWORK_MESSAGE_REGEX,
  tryReloadForChunkError
} from '@/services/helpers/ChunkLoadRecovery.js'
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent'

const NEW_RELIC_TIMEOUT_MS = 5000
const NEW_RELIC_CHECK_INTERVAL_MS = 100
const NEW_RELIC_MAX_ATTEMPTS =
  NEW_RELIC_TIMEOUT_MS / NEW_RELIC_CHECK_INTERVAL_MS

const SCRIPT_ERROR_MESSAGES = ['Script error.', 'Script error'] as const
const RESIZE_OBSERVER_MESSAGE =
  'ResizeObserver loop completed with undelivered notifications.'

// Bot and third-party noise that dominates JavaScriptError ingest (VIP-12034)
const IGNORED_AGENT_ERROR_REGEX = new RegExp(
  [
    'Object Not Found Matching Id', // Microsoft Safe Links scanner
    '_AutofillCallbackHandler', // Instagram/Facebook in-app browser
    'obApi', // Outbrain pixel
    'ResizeObserver loop',
    '^Script error\\.?$'
  ].join('|')
)

const V8_FRAME_REGEX = /^\s+at /
const STACK_FRAME_REGEX = /^(?:(?:global|module|eval) code|\S*)@/
const NATIVE_FRAME_REGEX = /\(<anonymous>\)$|\(index \d+\)$|@\[native code\]$/
// Safari masks extension script URLs; iOS in-app browsers inject user-script: frames
const EXTENSION_URL_REGEX =
  /-extension:\/\/|webkit-masked-url:|(?:^|@)user-script:/

// Read at load: a chunk failure on a page that was already reloaded for one
// means the deploy stays broken, so it is reported instead of dropped
const chunkReloadSpentOnLoad = isChunkReloadSpent()

interface NewRelicOptions {
  init: {
    distributed_tracing: { enabled: boolean }
    privacy: { cookies_enabled: boolean }
  }
  info: {
    beacon: string
    errorBeacon: string
    licenseKey: string
    applicationID: string
    sa: number
  }
  loader_config: {
    accountID: string
    trustKey: string
    agentID: string
    licenseKey: string
    applicationID: string
  }
}

function isScriptErrorMessage(msg: string | null): boolean {
  return (
    msg !== null && (SCRIPT_ERROR_MESSAGES as readonly string[]).includes(msg)
  )
}

function isResizeObserverMessage(msg: string | null): boolean {
  return msg === RESIZE_OBSERVER_MESSAGE
}

function waitForNewRelic(): Promise<void> {
  return new Promise((resolve) => {
    let attempts = 0
    const check = (): void => {
      attempts++
      if (globalThis.window?.newrelic?.noticeError) {
        resolve()
        return
      }
      if (attempts >= NEW_RELIC_MAX_ATTEMPTS) {
        console.warn(
          `New Relic failed to load within ${NEW_RELIC_TIMEOUT_MS}ms timeout`
        )
        resolve()
        return
      }
      setTimeout(check, NEW_RELIC_CHECK_INTERVAL_MS)
    }
    check()
  })
}

function isChunkReloadSpent(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(CHUNK_RELOAD_KEY) != null
  } catch {
    return false
  }
}

function getStackFrames(stack: string): string[] {
  const lines = stack.split('\n')
  // V8 starts the stack with the message, which may span several lines; only
  // its frames are indented. Firefox and Safari stacks hold frames only.
  const isV8 = lines.some((line) => V8_FRAME_REGEX.test(line))
  return lines
    .filter((line) =>
      isV8 ? V8_FRAME_REGEX.test(line) : STACK_FRAME_REGEX.test(line.trim())
    )
    .map((line) => line.trim())
    .filter((line) => !NATIVE_FRAME_REGEX.test(line))
}

function getFrames(error: unknown): string[] {
  const { stack, sourceURL } = (error ?? {}) as {
    stack?: unknown
    sourceURL?: unknown
  }
  if (typeof stack === 'string') return getStackFrames(stack)
  // The agent turns what it can't trust into an UncaughtError with a sourceURL
  // but no stack: an Error from a Safari extension's JS world (fails
  // instanceof), a non-Error throw, or a parse error from a window error event
  return typeof sourceURL === 'string' ? [sourceURL] : []
}

function isThrownInExtension(error: unknown, message: string): boolean {
  const frames = getFrames(error)
  if (frames.length === 0 || !EXTENSION_URL_REGEX.test(frames[0])) return false
  // An extension wrapping an API we call is the top frame of our own errors.
  // Safari drops async callers and V8 keeps 10 frames, so ours can be missing:
  // network failures, the usual case of a wrapped fetch, are kept regardless.
  return !frames.some(isAssetUrl) && !NETWORK_MESSAGE_REGEX.test(message)
}

/** Returning true tells the agent to drop the error. */
export function shouldDropAgentError(error: unknown): boolean {
  // The agent rethrows anything the callback throws, which breaks its error reporting
  try {
    const message = getErrorMessage(error)
    return (
      IGNORED_AGENT_ERROR_REGEX.test(message) ||
      (!chunkReloadSpentOnLoad && isChunkOrAssetFailure(error)) ||
      isThrownInExtension(error, message)
    )
  } catch (filterError) {
    console.warn('New Relic error filter failed', filterError)
    return false
  }
}

function setupGlobalErrorHandlers(): void {
  if (globalThis.window === undefined) return
  globalThis.window.onerror = (message, source, _lineno, _colno, error) => {
    const msg = typeof message === 'string' ? message : null
    if (isScriptErrorMessage(msg) && !source) return true
    if (isResizeObserverMessage(msg)) return true
    if (isChunkOrAssetFailure(error ?? message, source ?? undefined)) {
      // Suppress even when the once-per-session reload is already spent (VIP-12034)
      tryReloadForChunkError()
      return true
    }
    console.error(error ?? message)
    return false
  }
}

/**
 * Loads the New Relic Browser agent (RUM, page load, Ajax, JS errors). Errors it
 * captures pass through shouldDropAgentError; ApiController.logFrontendError
 * additionally sends handled errors to POST /v2/log/frontend-error.
 */
export const loadNewRelic = async (): Promise<void> => {
  if (import.meta.env.VITE_APP_NEW_RELIC_DEV_MODE === 'ON') {
    return
  }

  const licenseKey = `${import.meta.env.VITE_NEW_RELIC_LICENSE_KEY ?? ''}`
  const applicationID = `${import.meta.env.VITE_NEW_RELIC_APPLICATION_ID ?? ''}`
  if (
    !licenseKey ||
    !applicationID ||
    licenseKey === 'undefined' ||
    applicationID === 'undefined'
  ) {
    console.warn(
      'New Relic: missing or invalid VITE_NEW_RELIC_LICENSE_KEY or VITE_NEW_RELIC_APPLICATION_ID, skipping agent init'
    )
    return
  }

  const options: NewRelicOptions = {
    init: {
      distributed_tracing: { enabled: true },
      privacy: { cookies_enabled: true }
    },
    info: {
      beacon: 'bam.nr-data.net',
      errorBeacon: 'bam.nr-data.net',
      licenseKey,
      applicationID,
      sa: 1
    },
    loader_config: {
      accountID: `${import.meta.env.VITE_NEW_RELIC_ACCOUNT_ID ?? ''}`,
      trustKey: `${import.meta.env.VITE_NEW_RELIC_TRUST_KEY ?? ''}`,
      agentID: `${import.meta.env.VITE_NEW_RELIC_AGENT_ID ?? ''}`,
      licenseKey,
      applicationID
    }
  }

  const browserAgent = new BrowserAgent(options)
  // Before waiting, so errors captured while the agent loads are filtered too
  browserAgent.setErrorHandler(shouldDropAgentError)
  await waitForNewRelic()
  setupGlobalErrorHandlers()
}
